import type { KycDetail } from '@contract';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ImageViewer } from '@/components/image-viewer';
import { OpenRecord } from '@/components/queue/queue-parts';
import { ReasonDialog } from '@/components/reason-dialog';
import { FieldList } from '@/components/record-table';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loading } from '@/components/ui/loading';
import { adminQuery } from '@/lib/query';
import { useCan } from '@/lib/staff-context';

const DOCUMENTS: Record<string, string> = {
  'ghana-card': 'Ghana Card',
  passport: 'Passport',
  'voters-id': "Voter's ID",
  'national-id': 'National ID',
  'drivers-licence': "Driver's licence",
  'resident-id': 'Resident ID',
  'travel-document': 'Travel document',
};

export function documentLabel(type: string, country: string): string {
  return `${DOCUMENTS[type] ?? type}, ${country}`;
}

/**
 * Opens one verification. A retired Didit session is shown read only, as Sao
 * kept it. A Margins session (NIA) shows Margins' code and reference, NIA's
 * record and the selfie. Each opening is audited, so the session is read
 * only while the window is open.
 */
export function KycReviewButton({
  sessionId,
  title,
  label = 'Review',
  provider = 'didit',
}: Readonly<{
  sessionId: string;
  title: string;
  label?: string;
  /** The session's provider, when the caller knows it. */
  provider?: string;
}>) {
  const [open, setOpen] = useState(false);
  if (!useCan('kyc.detail')) return null;
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {provider === 'margins'
                ? 'What NIA answered through Margins for this Ghana Card and selfie.'
                : 'What Sao kept from this retired Didit session.'}{' '}
              Opening this is logged against you.
            </DialogDescription>
          </DialogHeader>
          {open ? (
            <KycDetailBody sessionId={sessionId} provider={provider} />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

function KycDetailBody({
  sessionId,
  provider,
}: Readonly<{ sessionId: string; provider: string }>) {
  // The first look is masked; a reason asks for the ID number and photos.
  const [reason, setReason] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const detail = useQuery({
    ...adminQuery(
      'kyc.detail',
      reason ? { session_id: sessionId, reason } : { session_id: sessionId },
    ),
    // Didit's photo links are short-lived; a second look asks again.
    staleTime: 0,
    gcTime: 0,
  });
  if (detail.isError) {
    return <Alert variant="destructive">{detail.error.message}</Alert>;
  }
  if (!detail.data) {
    return (
      <Loading label={provider === 'margins' ? 'Loading…' : 'Asking Didit…'} />
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {detail.data.masked ? (
        <Alert variant="warning">
          <div className="flex flex-col gap-3">
            <span>
              The ID number and photos are hidden. Revealing them needs a
              reason, which is logged against you.
            </span>
            <Button
              size="sm"
              variant="outline"
              className="w-fit"
              onClick={() => setAsking(true)}
            >
              Reveal the ID number and photos
            </Button>
          </div>
        </Alert>
      ) : null}
      <KycDetailView detail={detail.data} />
      <ReasonDialog
        open={asking}
        onOpenChange={setAsking}
        title="Reveal identity details"
        description="For this view only. The reveal and its reason are logged against you."
        confirmLabel="Reveal"
        minLength={10}
        pending={detail.isFetching}
        error={null}
        onConfirm={(value) => {
          setReason(value);
          setAsking(false);
        }}
      />
    </div>
  );
}

export function KycDetailView({ detail }: Readonly<{ detail: KycDetail }>) {
  if (detail.provider === 'margins')
    return <MarginsDetailView detail={detail} />;
  return <DiditRecordView detail={detail} />;
}

/** A retired Didit session as Sao kept it. Read only: Didit no longer decides anything. */
function DiditRecordView({ detail }: Readonly<{ detail: KycDetail }>) {
  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-2">
        <h3 className="font-semibold">Didit session (retired)</h3>
        <FieldList
          fields={[
            { key: 'status', label: 'Status', kind: 'status' },
            { key: 'document', label: 'Document' },
            { key: 'started_at', label: 'Started', kind: 'datetime' },
            { key: 'updated_at', label: 'Last changed', kind: 'datetime' },
          ]}
          record={{
            status: detail.status,
            document: documentLabel(
              detail.document_type,
              detail.issuing_country,
            ),
            started_at: detail.started_at,
            updated_at: detail.updated_at,
          }}
        />
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="font-semibold">What Didit read</h3>
        {detail.read ? (
          <FieldList
            fields={[
              { key: 'full_name', label: 'Name' },
              { key: 'date_of_birth', label: 'Born', kind: 'date' },
              { key: 'document_number', label: 'ID number', kind: 'mono' },
              { key: 'expiry_date', label: 'Expires', kind: 'date' },
              { key: 'gender', label: 'Gender' },
            ]}
            record={detail.read}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            Sao kept nothing from the document.
          </p>
        )}
      </section>
    </div>
  );
}

/**
 * A Margins (NIA) check as Sao stored it: why it waits for a person (a Ghana
 * Card another account holds), Margins' answer and reference, what NIA
 * returned, and the selfie.
 */
function MarginsDetailView({ detail }: Readonly<{ detail: KycDetail }>) {
  const code = detail.provider_code ?? '';
  const read = detail.read;
  const hasRead =
    read !== null && Object.values(read).some((value) => value !== null);
  const flags = detail.checks.flatMap((check) =>
    check.check === 'nia_face_match' ? [] : check.warnings,
  );
  return (
    <div className="flex flex-col gap-6">
      {flags.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="font-semibold">Why it needs a person</h3>
          <ul className="flex flex-col gap-2">
            {flags.map((flag) => (
              <li key={`${flag.risk}:${flag.detail ?? ''}`}>
                <Alert variant="warning">
                  <div className="flex flex-col gap-1">
                    <span className="font-medium">
                      {flag.summary ?? flag.risk}
                    </span>
                    <OtherAccount detail={flag.detail} />
                  </div>
                </Alert>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <section className="flex flex-col gap-2">
        <h3 className="font-semibold">NIA check through Margins</h3>
        <FieldList
          fields={[
            { key: 'answer', label: 'Answer' },
            { key: 'code', label: 'Margins code', kind: 'mono' },
            { key: 'reference', label: 'Reference', kind: 'mono' },
            { key: 'status', label: 'Status', kind: 'status' },
          ]}
          record={{
            answer: MARGINS_ANSWERS[code] ?? 'Unknown answer',
            code: detail.provider_code,
            reference: detail.provider_reference,
            status: detail.status,
          }}
        />
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="font-semibold">Selfie</h3>
        {detail.photos.selfie ? (
          <DocumentImages
            documents={{ selfie: detail.photos.selfie }}
            note={`This link stops working after ${Math.round(detail.expires_in_seconds / 60)} minutes.`}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            The selfie is no longer kept for this check.
          </p>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="font-semibold">What NIA returned</h3>
        {hasRead ? (
          <FieldList
            fields={[
              { key: 'full_name', label: 'Name' },
              { key: 'date_of_birth', label: 'Born', kind: 'date' },
              { key: 'document_number', label: 'Ghana Card', kind: 'mono' },
              { key: 'expiry_date', label: 'Card valid to', kind: 'date' },
              { key: 'gender', label: 'Gender' },
            ]}
            record={read}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            NIA returned no record.
          </p>
        )}
      </section>
    </div>
  );
}

const CUSTOMER_ID =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/;

/** A warning's detail; the other account it names opens as a record. */
function OtherAccount({ detail }: Readonly<{ detail: string | null }>) {
  if (!detail) return null;
  const id = CUSTOMER_ID.exec(detail)?.[0];
  if (!id) return <span className="text-sm">{detail}</span>;
  return (
    <span className="text-sm">
      Also used by{' '}
      <OpenRecord
        sheetRef={{ type: 'cust', id }}
        label="Open the other account"
        className="font-medium text-foreground underline"
      >
        the other account
      </OpenRecord>
      {detail.slice(detail.indexOf(id) + id.length)}
    </span>
  );
}

/** Margins' codes, in the words of its documentation. */
const MARGINS_ANSWERS: Record<string, string> = {
  '00': "Verified: the selfie matches NIA's photo",
  '01': 'Not matched',
  '02': 'Invalid card number or selfie',
  '03': 'On the NIA watch list: the person must visit an NIA office',
};

/** The thumbnails; one opens large in a window over the page. */
export function DocumentImages({
  documents,
  note,
}: Readonly<{ documents: Partial<Record<string, string>>; note: string }>) {
  const [shown, setShown] = useState<number | null>(null);
  const images = ORDER.flatMap((name) => {
    const url = documents[name];
    return url ? [{ name: PHOTO_NAMES[name], url }] : [];
  });
  return (
    <>
      <ul className="flex flex-wrap gap-3">
        {images.map((image, index) => (
          <li key={image.name}>
            <button
              type="button"
              aria-label={`Open ${image.name}`}
              onClick={() => setShown(index)}
            >
              <img
                src={image.url}
                alt={image.name}
                className="h-32 rounded-md border object-cover"
              />
            </button>
          </li>
        ))}
      </ul>
      <ImageViewer
        images={images}
        index={shown}
        onIndexChange={setShown}
        onClose={() => setShown(null)}
        note={note}
      />
    </>
  );
}

const ORDER = ['id_front', 'id_back', 'selfie', 'portrait'] as const;

const PHOTO_NAMES: Record<(typeof ORDER)[number], string> = {
  id_front: 'ID front',
  id_back: 'ID back',
  selfie: 'Selfie',
  portrait: 'Portrait',
};
